import React from "react";
import { Button, Tag } from "antd";
import { DiffOutlined, EyeOutlined } from "@ant-design/icons";
import { compact, isNil, map } from "lodash";
import { EMPTY } from "constants/common";
import { AiccPromptConfig } from "types/model/aiccPrompt";
import { appliesTemperature } from "../ModelSettings/appliesTemperature";
import { isModelRetired, modelAvailability } from "../ModelSettings/availability";
import { PromptSettingChange, PromptTextDiff } from "../promptDiff";
import {
  DIFF_ADDED_LINES,
  DIFF_CALLOUT_TITLE,
  DIFF_IDENTICAL,
  DIFF_REMOVED_LINES,
  DIFF_VIEW_CHANGES,
  DISCARD_BUTTON,
  REFERENCE_ABSENT,
  REFERENCE_VIEW_TEXT,
} from "../configs";
import styles from "./Reference.module.scss";

interface Props {
  title: string;
  config?: AiccPromptConfig;
  modelName?: string;
  isDeprecated?: boolean;
  shutdownAt?: string;
  description: string;
  diff?: PromptTextDiff;
  settingChanges?: PromptSettingChange[];
  onView?: () => void;
  onDiscard?: () => void;
}

function DefaultReferencePanel({
  title,
  config,
  modelName,
  isDeprecated,
  shutdownAt,
  description,
  diff,
  settingChanges,
  onView,
  onDiscard,
}: Props) {
  if (!config) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelTitle}>{title}</div>
        <div className={styles.panelDescription}>{description}</div>
        <div className={styles.panelEmpty}>{REFERENCE_ABSENT}</div>
      </div>
    );
  }

  const settingFacts = compact([
    isNil(config.temperature) || !appliesTemperature(config.reasoningEffort)
      ? ""
      : `Temperature ${config.temperature.toFixed(1)}`,
    config.reasoningEffort ? `Effort ${config.reasoningEffort}` : "",
  ]);
  const availability = modelAvailability({ isDeprecated, shutdownAt });
  // Three states, and red is only the last: a supported model, one retiring but still answering, and
  // one past its shutdown date — which is the only one with something to fix. See `ReadOnlySettingsBar`.
  const retired = isModelRetired({ isDeprecated, shutdownAt });
  const modelBadgeClass = !isDeprecated
    ? styles.modelBadge
    : retired
    ? styles.modelBadgeRetired
    : styles.modelBadgeAging;
  const hasSettingChanges = !!settingChanges?.length;
  const isIdentical = !!diff?.isIdentical;
  const hasTextDiff = !!diff && !diff.isIdentical;
  const hasChanges = hasTextDiff || hasSettingChanges;

  return (
    <div className={styles.panel}>
      <div className={styles.panelTitle}>{title}</div>
      <div className={styles.panelDescription}>{description}</div>

      {(!!config.modelId || !isNil(config.temperature)) && (
        <div className={styles.referenceMeta}>
          <Tag className={modelBadgeClass}>{modelName || config.modelId || EMPTY}</Tag>
          {!!availability && (
            <span className={retired ? styles.retiredLabel : styles.availabilityLabel}>{availability}</span>
          )}
          {!!settingFacts.length && <span className={styles.referenceTemperature}>{settingFacts.join(" · ")}</span>}
        </div>
      )}

      {hasChanges ? (
        <div className={styles.diffCallout}>
          <div className={styles.diffCalloutTitle}>
            <DiffOutlined className={styles.diffCalloutIcon} />
            {DIFF_CALLOUT_TITLE}
          </div>

          {!!diff && !diff.isIdentical && (
            <div className={styles.diffCounts}>
              <span className={styles.diffCountAdded}>{DIFF_ADDED_LINES(diff.added)}</span>
              <span className={styles.diffCountRemoved}>{DIFF_REMOVED_LINES(diff.removed)}</span>
            </div>
          )}

          {hasSettingChanges && (
            <div className={styles.settingChanges}>
              {map(settingChanges, ({ label, to }) => (
                <div key={label} className={styles.settingChange}>
                  {`${label} → `}
                  <span className={styles.settingTo}>{to}</span>
                </div>
              ))}
            </div>
          )}

          <div className={styles.diffActions}>
            {!!onView && (
              <Button shape="round" size="small" icon={<EyeOutlined />} className={styles.viewButton} onClick={onView}>
                {DIFF_VIEW_CHANGES}
              </Button>
            )}
            {!!onDiscard && (
              <Button type="link" className={styles.discardButton} onClick={onDiscard}>
                {DISCARD_BUTTON}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.referenceFooter}>
          {isIdentical && <div className={styles.panelEmpty}>{DIFF_IDENTICAL}</div>}
          {!!onView && (
            <Button shape="round" size="small" icon={<EyeOutlined />} className={styles.viewButton} onClick={onView}>
              {REFERENCE_VIEW_TEXT}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default DefaultReferencePanel;
